import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import notify from '../utils/notify';

import StripePaymentModal from '../components/Stripe/StripePaymentModal';
import AddFundsModal from '../components/Credits/ApplyFundsModal';
import TagSelector from '../components/shared/TagSelector';
import SendEmailModal from '../components/common/SendEmailModal';

import { ArrowLeft, Banknote, CreditCard, Download, PlusCircle, Receipt, X, Pencil, Trash2, Lock, Send } from 'lucide-react';

import '../component-styles/RetainersDetail.css';
import '../component-styles/TimeBillingInvoiceView.css';
import '../component-styles/QuickbooksActions.css';
import API_BASE from '../utils/api.js';
import { roundCurrency, sanitizeCurrencyInput } from '../utils/currency.js';
import { getTBInvoiceSections, isTBInvoiceListLayout } from '../utils/timeBillingShared.js';
import {
  depositRequestLabel,
  depositStillDue,
  formatInvoiceDate,
  toDateInputValue,
} from '../utils/invoiceAdjustments.js';
import QuickBooksConnectModal from '../components/utils/QuickbooksConnectModal';
import ConfirmModal from '../components/utils/ConfirmModal';
import { isQuickBooksConnectionError } from '../utils/quickbooksErrors';

/* --------------------------------------------------------
   HELPERS
-------------------------------------------------------- */
function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

function formatPlainNumber(value, fractionDigits = 2) {
  const num = Number(value);
  if (!Number.isFinite(num)) return Number(0).toFixed(fractionDigits);

  return num.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

async function loadImageAsDataUrl(url) {
  if (!url) return null;

  try {
    const res = await fetch(url);
    if (!res.ok) return null;

    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('Unable to load logo for PDF export:', err);
    return null;
  }
}

function getQuickBooksInvoiceUrl(invoiceId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';

  return `${base}/app/invoice?txnId=${invoiceId}`;
}

/* --------------------------------------------------------
   COMPONENT
-------------------------------------------------------- */
export default function TimeBillingInvoiceViewPage() {
  const { projectId, tbInvoiceId } = useParams();
  const navigate = useNavigate();

  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem('idToken');

  const apiUrl = API_BASE;

  const [invoice, setInvoice] = useState(null);

  const [showStripe, setShowStripe] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [tags, setTags] = useState([]);

  const [syncingQB, setSyncingQB] = useState(false);
  const [convertingPO, setConvertingPO] = useState(false);
  const [showQBModal, setShowQBModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', destructive: false, pendingAction: null });

  function askConfirm({ title, message, confirmLabel, destructive, action }) {
    setConfirmModal({ open: true, title, message, confirmLabel, destructive: !!destructive, pendingAction: action });
  }

  async function handleConfirm() {
    const action = confirmModal.pendingAction;
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
    if (action) await action();
  }

  function handleCancel() {
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
  }

  const [manualForm, setManualForm] = useState({
    amount: '',
    method: 'cash',
    reference: '',
  });

  const [editPayment, setEditPayment] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', method: 'cash', paymentDate: '', reference: '' });
  const [confirmDeletePayment, setConfirmDeletePayment] = useState(null);

  const [editingItem, setEditingItem] = useState(null);
  const [editItemForm, setEditItemForm] = useState({ hours: '', rate: '', description: '' });

  const [detailsForm, setDetailsForm] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);

  const [sendEmailModal, setSendEmailModal] = useState({ open: false, email: '', publicUrl: '' });
  const [generatingLink, setGeneratingLink] = useState(false);

  /* --------------------------------------------------------
     LOAD INVOICE
  -------------------------------------------------------- */
  async function loadInvoice() {
    try {
      const res = await fetch(`${apiUrl}/api/timebilling-invoices/${userId}/${tbInvoiceId}`, {
        cache: 'no-store',
      });

      const data = await res.json();

      if (!res.ok) {
        notify(data.error || 'Failed to load invoice');
        return;
      }

      data.paymentHistory = data.paymentHistory || [];
      setInvoice(data);
      setTags(Array.isArray(data.tags) ? data.tags : []);
    } catch (err) {
      console.error('Failed to load invoice:', err);
    }
  }

  useEffect(() => {
    loadInvoice();
  }, [tbInvoiceId]);

  if (!invoice) {
    return <div style={{ padding: 24 }}>Loading invoice...</div>;
  }

  /* --------------------------------------------------------
     QUICKBOOKS
  -------------------------------------------------------- */
  const qb = invoice.quickbooks;
  const isSynced = Boolean(qb?.invoiceId);

  async function createQuickBooksInvoice() {
    if (!tbInvoiceId || !projectId) {
      notify('Invoice must be saved before syncing to QuickBooks.');
      return;
    }

    if (!token) {
      notify('You must be signed in to sync with QuickBooks.');
      return;
    }

    try {
      setSyncingQB(true);

      const res = await fetch(`${apiUrl}/api/quickbooks/create-timebilling-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tbInvoiceId, projectId }),
      });

      const rawBody = await res.text();
      let data = {};
      try { data = rawBody ? JSON.parse(rawBody) : {}; } catch { data = { error: rawBody || null }; }

      if (!res.ok) {
        if (isQuickBooksConnectionError(data)) {
          setShowQBModal(true);
          return;
        }

        const message =
          data?.error ||
          data?.details?.error ||
          data?.details?.Fault?.Error?.[0]?.Detail ||
          rawBody ||
          'Failed to create QuickBooks invoice.';
        const alreadySynced = /already synced/i.test(message);
        if (alreadySynced) {
          const updateRes = await fetch(`${apiUrl}/api/quickbooks/update-timebilling-invoice`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ tbInvoiceId, projectId }),
          });
          const updateRaw = await updateRes.text();
          let updateData = {};
          try { updateData = updateRaw ? JSON.parse(updateRaw) : {}; } catch { updateData = { error: updateRaw || null }; }
          if (!updateRes.ok) {
            notify(
              updateData?.error ||
                updateData?.details?.error ||
                updateData?.details?.Fault?.Error?.[0]?.Detail ||
                updateRaw ||
                'Failed to update QuickBooks invoice.'
            );
            return;
          }
        } else {
          console.error('QuickBooks error:', data);
          notify(message);
          return;
        }
      }

      notify('QuickBooks invoice synced.');
      loadInvoice();
    } catch (err) {
      console.error('QuickBooks sync error:', err);
      notify('QuickBooks sync failed.');
    } finally {
      setSyncingQB(false);
    }
  }

  // Applies the change optimistically and rolls back just those keys on failure.
  async function updateDisplay(patch, errorMessage) {
    const previous = invoice.display || {};
    const applyDisplay = (changes) =>
      setInvoice((prev) =>
        prev ? { ...prev, display: { ...(prev.display || {}), ...changes } } : prev
      );

    applyDisplay(patch);

    try {
      const res = await fetch(
        `${apiUrl}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          tbInvoiceId
        )}/display`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        }
      );

      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }
    } catch (err) {
      console.error('Failed to update invoice display setting:', err);
      applyDisplay(Object.fromEntries(Object.keys(patch).map((key) => [key, previous[key]])));
      notify(errorMessage);
    }
  }

  function toggleEmployeeNames() {
    updateDisplay({ hideEmployeeNames: !hideEmployeeNames }, 'Unable to update employee visibility setting.');
  }

  function toggleLayout() {
    updateDisplay({ layout: isListLayout ? 'grouped' : 'list' }, 'Unable to update invoice layout.');
  }

  async function saveTags(newTags) {
    setTags(newTags);
    try {
      await fetch(
        `${apiUrl}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(tbInvoiceId)}/tags`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: newTags }),
        }
      );
    } catch (err) {
      console.error('Failed to save tags:', err);
      notify('Unable to save tags.');
    }
  }

  async function convertToPurchaseOrder() {
    if (!userId || !projectId || !tbInvoiceId) return;

    askConfirm({
      title: 'Create Purchase Order',
      message: 'Create a purchase order from this time billing invoice? A new draft PO will be generated from the invoice line items.',
      confirmLabel: 'Create PO',
      action: async () => {
        try {
          setConvertingPO(true);

          const res = await fetch(
            `${apiUrl}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
              projectId
            )}/${encodeURIComponent(tbInvoiceId)}/convert-to-po`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
            }
          );

          const data = await res.json();
          if (!res.ok) {
            notify(data.error || 'Failed to convert to purchase order.');
            return;
          }

          const poId = data.openedPoId || data.poId || data.purchaseOrder?.poId;
          if (poId) {
            navigate(`/projects/${projectId}/purchase-orders/${poId}`);
          }
        } catch (err) {
          console.error('Failed to convert time billing invoice to purchase order:', err);
          notify('Unable to convert to purchase order.');
        } finally {
          setConvertingPO(false);
        }
      },
    });
  }

  /* --------------------------------------------------------
     PDF DOWNLOAD
  -------------------------------------------------------- */
  async function downloadPDF() {
    const doc = new jsPDF('p', 'pt', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;
    const footerHeight = 36;
    const brand = [165, 210, 125];

    let y = 40;

    const logoData = await loadImageAsDataUrl(company.logo);

    // ── HEADER: logo + company info left, invoice # right ──────────────────
    const logoSize = 52;
    if (logoData) {
      doc.addImage(logoData, 'PNG', marginX, y, logoSize, logoSize, undefined, 'FAST');
    }

    const textX = logoData ? marginX + logoSize + 12 : marginX;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(33, 37, 41);
    doc.text(company.name || 'InteriorFolio', textX, y + 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(110, 110, 110);
    const companyInfoLines = [
      [addressLine, cityLine].filter(Boolean).join(', '),
      contactLine,
    ].filter(Boolean);
    if (companyInfoLines.length) doc.text(companyInfoLines, textX, y + 27);

    // Invoice number — brand color, right-aligned
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...brand);
    doc.text(`Invoice #${invoice.invoiceNumber || ''}`, pageWidth - marginX, y + 16, { align: 'right' });

    y = Math.max(y + logoSize, y + 50) + 16;

    // ── DIVIDER ─────────────────────────────────────────────────────────────
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.75);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 18;

    // ── BILL TO + DATES ──────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.text('Bill to:', marginX, y);

    const billToLines = [
      invoice.billToName,
      invoice.billToStreet,
      [invoice.billToCity, invoice.billToState, invoice.billToZip].filter(Boolean).join(' '),
      invoice.billToPhone,
      invoice.billToEmail,
    ].filter(Boolean);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(33, 37, 41);
    if (billToLines.length) {
      doc.text(billToLines[0], marginX, y + 13);
      if (billToLines.length > 1) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(90, 90, 90);
        doc.text(billToLines.slice(1), marginX, y + 25);
      }
    } else if (projectName) {
      doc.text(projectName, marginX, y + 13);
    }

    // Dates block — right side
    const datesLabelX = pageWidth - marginX - 100;
    const datesValueX = pageWidth - marginX;

    const dateRows = [['Date of Issue:', invoiceDate]];
    if (dueDate) dateRows.push(['Due Date:', dueDate]);
    if (depositDue > 0) dateRows.push(['Deposit Due:', `$${formatPlainNumber(depositDue)}`]);
    if (remainingBalance > 0) dateRows.push(['Balance Due:', `$${formatPlainNumber(remainingBalance)}`]);

    let dateY = y;
    dateRows.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      doc.text(label, datesLabelX, dateY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(33, 37, 41);
      doc.text(value, datesValueX, dateY, { align: 'right' });
      dateY += 15;
    });

    const billToHeight = billToLines.length > 1 ? 14 + (billToLines.length - 1) * 12 : 20;
    y += Math.max(billToHeight + 14, dateY - y + 8);

    // ── DIVIDER ─────────────────────────────────────────────────────────────
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.75);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 20;

    // ── GROUP TABLES ─────────────────────────────────────────────────────────
    for (const [index, group] of sections.entries()) {
      if (group.heading) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(33, 37, 41);
        doc.text(group.heading, marginX, y);
        y += 12;
      }

      autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [[...(isListLayout ? ['Activity'] : []), 'Description', 'Hours', 'Rate', 'Amount']],
        body: (group.items || []).map((item) => [
          ...(isListLayout ? [item.activity] : []),
          `${item.description || ''}${!hideEmployeeNames && item.employee ? ` – ${item.employee}` : ''}`,
          formatPlainNumber(item.hours),
          formatPlainNumber(item.rate),
          formatPlainNumber(item.total),
        ]),
        theme: 'plain',
        styles: {
          fontSize: 9,
          cellPadding: { top: 5, right: 8, bottom: 5, left: 8 },
          lineColor: [220, 220, 220],
          lineWidth: { bottom: 0.5 },
          textColor: [33, 37, 41],
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: [248, 249, 250],
          textColor: [90, 90, 90],
          fontStyle: 'bold',
          fontSize: 8.5,
          lineWidth: { bottom: 0.75 },
          lineColor: [200, 200, 200],
        },
        // Same 515pt total width either way; list layout splits the
        // description column to make room for Activity.
        columnStyles: isListLayout
          ? {
              0: { cellWidth: 110 },
              1: { cellWidth: 210 },
              2: { halign: 'right', cellWidth: 65 },
              3: { halign: 'right', cellWidth: 65 },
              4: { halign: 'right', cellWidth: 65 },
            }
          : {
              0: { cellWidth: 320 },
              1: { halign: 'right', cellWidth: 65 },
              2: { halign: 'right', cellWidth: 65 },
              3: { halign: 'right', cellWidth: 65 },
            },
        didParseCell: (data) => {
          if (data.section === 'head') {
            data.cell.styles.lineWidth = { bottom: 0.75 };
          }
        },
      });

      y = doc.lastAutoTable.finalY + (index < sections.length - 1 ? 22 : 14);
    }

    // ── SUMMARY ──────────────────────────────────────────────────────────────
    const regularRows = [
      ['Sub Total', formatPlainNumber(subtotal)],
      ...(contingencyAmount > 0 ? [[contingencyLabel, formatPlainNumber(contingencyAmount)]] : []),
      ...(discountAmount > 0 ? [[discountLabel, `-${formatPlainNumber(discountAmount)}`]] : []),
      ['Sales Tax', formatPlainNumber(taxAmount)],
      ['Total', formatPlainNumber(totalAmount)],
      ...(depositRequested > 0
        ? [[depositRequestLabel(invoice.depositRequest), formatPlainNumber(depositRequested)]]
        : []),
      ['Payments', formatPlainNumber(paymentsAmount)],
    ];

    const estimatedSummaryHeight = 44 + regularRows.length * 14;
    if (y + estimatedSummaryHeight > pageHeight - footerHeight - 16) {
      doc.addPage();
      y = 40;
    }

    const summaryLabelX = pageWidth - marginX - 130;
    const summaryValueX = pageWidth - marginX;

    // Subtle separator spanning only the summary column
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.5);
    doc.line(summaryLabelX - 20, y, pageWidth - marginX, y);
    y += 12;

    doc.setFontSize(9);
    regularRows.forEach(([label, value]) => {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(90, 90, 90);
      doc.text(label, summaryLabelX, y, { align: 'right' });
      doc.setTextColor(33, 37, 41);
      doc.text(value, summaryValueX, y, { align: 'right' });
      y += 14;
    });

    // Balance Due — emphasized with brand accent
    y += 4;
    doc.setDrawColor(...brand);
    doc.setLineWidth(0.75);
    doc.line(summaryLabelX - 20, y - 2, pageWidth - marginX, y - 2);
    y += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...brand);
    doc.text('Balance Due', summaryLabelX, y + 10, { align: 'right' });
    doc.setTextColor(33, 37, 41);
    doc.text(`$${formatPlainNumber(remainingBalance)}`, summaryValueX, y + 10, { align: 'right' });

    // ── FOOTER ───────────────────────────────────────────────────────────────
    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);

      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.line(marginX, pageHeight - footerHeight, pageWidth - marginX, pageHeight - footerHeight);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(170, 170, 170);
      doc.text('Invoice provided by InteriorFolio', pageWidth / 2, pageHeight - footerHeight + 14, { align: 'center' });

      if (pageCount > 1) {
        doc.text(`Page ${page} of ${pageCount}`, pageWidth - marginX, pageHeight - footerHeight + 14, { align: 'right' });
      }
    }

    doc.save(`${invoice.invoiceNumber || 'time-billing-invoice'}.pdf`);
  }

  /* --------------------------------------------------------
     COMPUTED
  -------------------------------------------------------- */
  const company = invoice.company || {};
  const addressLine = [company.addressLine1, company.addressLine2].filter(Boolean).join(', ');
  const cityLine = [company.city, company.state, company.zip].filter(Boolean).join(' ');
  const contactLine = [company.phone, company.website || company.email].filter(Boolean).join('  ');
  const clientAddressLines = [addressLine, cityLine].filter(Boolean);

  const hideEmployeeNames = Boolean(invoice.display?.hideEmployeeNames);
  const isListLayout = isTBInvoiceListLayout(invoice);
  const sections = getTBInvoiceSections(invoice);

  const allItems = (invoice.groups || []).flatMap((group) => group.items || []);
  const totalHours = allItems.reduce((sum, item) => sum + Number(item.hours || 0), 0);
  const totalAmount = roundCurrency(invoice.totals?.total || 0);
  const remainingBalance = roundCurrency(invoice.remainingBalance ?? totalAmount);
  const paymentsAmount = roundCurrency(Math.max(totalAmount - remainingBalance, 0));
  const printedDate = formatDate(new Date());
  const invoiceDate = formatInvoiceDate(invoice.invoiceDate || invoice.createdAt);
  const dueDate = formatInvoiceDate(invoice.dueDate);
  const projectName = invoice.projectName || invoice.projectTitle || invoice.projectId || projectId;
  const invoiceDescription =
    invoice.description ||
    invoice.invoiceDescription ||
    (invoice.groups || [])
      .map((group) => group.heading)
      .filter(Boolean)
      .join(', ');

  const status =
    remainingBalance <= 0 ? 'Paid' : invoice.paymentHistory.length ? 'Partial' : 'Open';
  const hasPayments = invoice.paymentHistory.length > 0;

  const subtotal = Number(invoice.totals?.subtotal || 0);
  const taxAmount = Number(invoice.totals?.tax || 0);
  const taxRate = invoice.tax?.rate || 0;
  const hasTax = taxAmount > 0;

  const contingencyAmount = Number(invoice.totals?.contingency || 0);
  const contingencyLabel = invoice.contingency
    ? `${invoice.contingency.label} (${invoice.contingency.percent}%)`
    : 'Contingency';
  const discountAmount = Number(invoice.totals?.discount || 0);
  const discountLabel =
    invoice.discount?.type === 'percent' ? `Discount (${invoice.discount.value}%)` : 'Discount';
  const depositRequested = Number(invoice.totals?.depositRequested || 0);
  const depositDue = remainingBalance > 0 ? depositStillDue(depositRequested, paymentsAmount) : 0;

  /* --------------------------------------------------------
     INVOICE DETAILS (dates, contingency, discount, deposit)
  -------------------------------------------------------- */
  function openDetails() {
    setDetailsForm({
      invoiceDate: toDateInputValue(invoice.invoiceDate || invoice.createdAt),
      dueDate: toDateInputValue(invoice.dueDate),
      contingencyLabel: invoice.contingency?.label || 'Contingency',
      contingencyPercent: invoice.contingency?.percent ? String(invoice.contingency.percent) : '',
      discountType: invoice.discount?.type || 'amount',
      discountValue: invoice.discount?.value ? String(invoice.discount.value) : '',
      depositType: invoice.depositRequest?.type || 'percent',
      depositValue: invoice.depositRequest?.value ? String(invoice.depositRequest.value) : '',
    });
  }

  async function saveDetails() {
    const form = detailsForm;
    if (form.dueDate && form.invoiceDate && form.dueDate < form.invoiceDate) {
      notify('Due date cannot be before the invoice date.');
      return;
    }

    setSavingDetails(true);
    try {
      const res = await fetch(`${apiUrl}/api/timebilling-invoices/${tbInvoiceId}/details`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          userid: userId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          invoiceDate: form.invoiceDate,
          dueDate: form.dueDate,
          contingency: { label: form.contingencyLabel, percent: Number(form.contingencyPercent || 0) },
          discount: { type: form.discountType, value: Number(form.discountValue || 0) },
          depositRequest: { type: form.depositType, value: Number(form.depositValue || 0) },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        notify(data.error || 'Failed to update invoice details.');
        return;
      }
      setDetailsForm(null);
      loadInvoice();
    } catch (err) {
      console.error('Failed to update invoice details:', err);
      notify('Failed to update invoice details.');
    } finally {
      setSavingDetails(false);
    }
  }

  /* --------------------------------------------------------
     MANUAL PAYMENT
  -------------------------------------------------------- */
  async function applyManualPayment() {
    await fetch(`${apiUrl}/api/timebilling-invoices/${tbInvoiceId}/apply-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        userid: userId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        userId,
        amount: roundCurrency(manualForm.amount),
        paymentType: 'manual',
        method: manualForm.method,
        reference: manualForm.reference,
      }),
    });

    setShowManual(false);
    setManualForm({ amount: '', method: 'cash', reference: '' });
    loadInvoice();
  }

  function openEditPayment(p) {
    setEditPayment(p);
    setEditForm({
      amount: String(p.amount),
      method: p.method || p.paymentType || 'cash',
      paymentDate: p.date ? p.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      reference: p.reference || '',
    });
  }

  async function saveEditPayment() {
    const amt = roundCurrency(editForm.amount);
    if (!amt || amt <= 0) return;

    await fetch(`${apiUrl}/api/timebilling-invoices/${tbInvoiceId}/payments/${editPayment.paymentId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        userid: userId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ amount: amt, method: editForm.method, reference: editForm.reference, paymentDate: editForm.paymentDate }),
    });

    setEditPayment(null);
    loadInvoice();
  }

  function openEditItem(item) {
    setEditingItem(item);
    setEditItemForm({
      hours: String(item.hours),
      rate: String(item.rate),
      description: item.description || '',
    });
  }

  async function saveEditItem() {
    const hours = parseFloat(editItemForm.hours);
    const rate = parseFloat(editItemForm.rate);

    if (!Number.isFinite(hours) || hours < 0) { notify('Enter a valid hours value.'); return; }
    if (!Number.isFinite(rate) || rate < 0) { notify('Enter a valid rate value.'); return; }

    const res = await fetch(
      `${apiUrl}/api/timebilling-invoices/${userId}/${tbInvoiceId}/entries/${encodeURIComponent(editingItem.sortKey)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours, rate, description: editItemForm.description }),
      }
    );

    const data = await res.json();
    if (!res.ok) { notify(data.error || 'Failed to update entry.'); return; }

    setEditingItem(null);
    loadInvoice();
  }

  async function deletePaymentEntry(paymentId) {
    await fetch(`${apiUrl}/api/timebilling-invoices/${tbInvoiceId}/payments/${paymentId}`, {
      method: 'DELETE',
      headers: {
        userid: userId,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    loadInvoice();
  }

  /* --------------------------------------------------------
     SEND TO CLIENT
  -------------------------------------------------------- */
  async function openSendToClient() {
    try {
      setGeneratingLink(true);
      let shareToken = invoice.shareToken;
      if (!shareToken) {
        const res = await fetch(
          `${apiUrl}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(tbInvoiceId)}/share`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' } }
        );
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const data = await res.json();
        shareToken = data.shareToken;
      }
      if (!shareToken) throw new Error('No shareToken returned');
      const publicUrl = `${window.location.origin}/public/timebilling-invoice/${shareToken}`;
      const email = invoice.billToEmail || '';
      setSendEmailModal({ open: true, email, publicUrl });
    } catch (e) {
      console.error('Failed to get TB invoice share link', e);
      notify('Failed to generate invoice link.');
    } finally {
      setGeneratingLink(false);
    }
  }

  /* --------------------------------------------------------
     RENDER
  -------------------------------------------------------- */
  return (
    <>
      <div className="tb-action-bar no-pdf">
        <button className="tb-btn-back" onClick={() => navigate(`/timebilling/${projectId}`)}>
          <ArrowLeft size={16} /> Back to Time Billing
        </button>
      </div>

      <QuickBooksConnectModal
        open={showQBModal}
        onClose={() => setShowQBModal(false)}
        onConnected={createQuickBooksInvoice}
        mode="invoice"
      />

      <div className="rd-container tb-invoice-view">
        <div className="rd-header-grid">
          <div className="rd-company-section">
            <img
              src={company.logo || '/default-if-logo.png'}
              alt="Company Logo"
              className="rd-company-logo"
            />
            <div>
              <div className="rd-company-name">{company.name || 'InteriorFolio'}</div>
              <div className="rd-company-sub">
                {addressLine && <div>{addressLine}</div>}
                {cityLine && <div>{cityLine}</div>}
                {company.phone && <div>{company.phone}</div>}
                {company.email && <div>{company.email}</div>}
              </div>
            </div>
          </div>

          <div className="rd-meta-section">
            <span className={`rd-status-pill rd-status-${status.toLowerCase()}`}>{status}</span>

            <div>
              <div className="rd-meta-label">Invoice Number</div>
              <div className="rd-meta-value">{invoice.invoiceNumber}</div>
            </div>

            <div className="rd-meta-date">Date: {invoiceDate}</div>
            {dueDate && <div className="rd-meta-date">Due: {dueDate}</div>}
          </div>
        </div>

        {(invoice.billToName || invoice.billToStreet || invoice.billToEmail) && (
          <div className="tb-bill-to-section">
            <div className="tb-bill-to-label">Bill To</div>
            {invoice.billToName && <div className="tb-bill-to-name">{invoice.billToName}</div>}
            {invoice.billToStreet && <div className="tb-bill-to-line">{invoice.billToStreet}</div>}
            {[invoice.billToCity, invoice.billToState, invoice.billToZip].filter(Boolean).length > 0 && (
              <div className="tb-bill-to-line">
                {[invoice.billToCity, invoice.billToState, invoice.billToZip].filter(Boolean).join(' ')}
              </div>
            )}
            {invoice.billToEmail && <div className="tb-bill-to-line">{invoice.billToEmail}</div>}
            {invoice.billToPhone && <div className="tb-bill-to-line">{invoice.billToPhone}</div>}
          </div>
        )}

        <div className="tb-invoice-content">
          <div className="tb-invoice-main">
            {sections.map((group) => (
              <div key={group.heading}>
                {group.heading && <div className="tb-group-heading">{group.heading}</div>}

                <table className="rd-items-table">
                  <thead>
                    <tr>
                      {isListLayout && <th>Activity</th>}
                      <th>Description</th>
                      {!hideEmployeeNames && <th>Employee</th>}
                      <th>Hours</th>
                      <th>Rate</th>
                      <th>Total</th>
                      {status === 'Open' && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(group.items || []).map((item) => (
                      <tr key={item.id}>
                        {isListLayout && <td>{item.activity}</td>}
                        <td>{item.description}</td>
                        {!hideEmployeeNames && <td>{item.employee}</td>}
                        <td>{item.hours}</td>
                        <td>{formatCurrency(item.rate)}</td>
                        <td>{formatCurrency(item.total)}</td>
                        {status === 'Open' && (
                          <td>
                            <button
                              className="rd-icon-btn"
                              title="Edit entry"
                              onClick={() => openEditItem(item)}
                            >
                              <Pencil size={13} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          <aside className="rd-totals-box tb-invoice-sidebar">
            <div className="rd-totals-row">
              <span>Total Hours</span>
              <span>{totalHours}</span>
            </div>

            <div className="rd-totals-row">
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>

            {contingencyAmount > 0 && (
              <div className="rd-totals-row">
                <span>{contingencyLabel}</span>
                <span>{formatCurrency(contingencyAmount)}</span>
              </div>
            )}

            {discountAmount > 0 && (
              <div className="rd-totals-row">
                <span>{discountLabel}</span>
                <span>−{formatCurrency(discountAmount)}</span>
              </div>
            )}

            {hasTax && (
              <div className="rd-totals-row">
                <span>Tax {taxRate ? `(${Number((taxRate * 100).toFixed(3))}%)` : ''}</span>
                <span>{formatCurrency(taxAmount)}</span>
              </div>
            )}

            {hasPayments && (
              <>
                <div className="rd-totals-row">
                  <span>Invoice Total</span>
                  <span>{formatCurrency(totalAmount)}</span>
                </div>
                <div className="rd-totals-row">
                  <span>Payments</span>
                  <span>−{formatCurrency(paymentsAmount)}</span>
                </div>
              </>
            )}

            <div className="rd-totals-row rd-total">
              <span>Total Due</span>
              <span>{formatCurrency(remainingBalance)}</span>
            </div>

            {depositRequested > 0 && (
              <div className="rd-totals-row">
                <span>{depositRequestLabel(invoice.depositRequest)}</span>
                <span>{formatCurrency(depositRequested)}</span>
              </div>
            )}

            {depositRequested > 0 && hasPayments && remainingBalance > 0 && (
              <div className="rd-totals-row">
                <span>Deposit Still Due</span>
                <span>{formatCurrency(depositDue)}</span>
              </div>
            )}

            {remainingBalance > 0 && (
              <button className="rd-pay-btn no-pdf" onClick={() => setShowPaymentChoice(true)}>
                <PlusCircle size={16} style={{ marginRight: 6 }} />
                Make a Payment
              </button>
            )}
          </aside>
        </div>

        <div className="rd-payments-section no-pdf">
          <h3>Tags</h3>
          <TagSelector selected={tags} onChange={saveTags} />
        </div>

        <div className="rd-payments-section">
          <h3>Payments</h3>

          {invoice.paymentHistory.length === 0 ? (
            <div className="rd-empty">No payments yet.</div>
          ) : (
            <table className="rd-payments-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Method</th>
                  <th>Reference</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoice.paymentHistory.map((payment) => (
                  <tr key={payment.paymentId}>
                    <td>{payment.date?.slice(0, 10)}</td>
                    <td>{payment.method || payment.paymentType}</td>
                    <td className="rd-ref-cell">{payment.reference || '-'}</td>
                    <td>{formatCurrency(payment.amount)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {payment.paymentType === 'stripe' ? (
                        <Lock size={13} style={{ color: '#aaa', verticalAlign: 'middle' }} title="Stripe payment — locked" />
                      ) : (
                        <>
                          <button className="rd-icon-btn" title="Edit payment" onClick={() => openEditPayment(payment)} style={{ marginRight: 4 }}>
                            <Pencil size={13} />
                          </button>
                          <button className="rd-icon-btn rd-icon-btn--danger" title="Delete payment" onClick={() => setConfirmDeletePayment(payment)}>
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="tb-invoice-bottom-actions no-pdf">
          <button className="tb-btn-pdf" onClick={downloadPDF}>
            <Download size={16} /> Download PDF
          </button>

          <button className="tb-btn-send-client" onClick={openSendToClient} disabled={generatingLink}>
            <Send size={16} /> {generatingLink ? 'Generating link...' : 'Send to Client'}
          </button>

          <button className="tb-invoice-btn-secondary" onClick={openDetails}>
            <Pencil size={14} /> Edit Invoice Details
          </button>

          <button className="tb-invoice-btn-secondary" onClick={toggleEmployeeNames}>
            {hideEmployeeNames ? 'Show Employee Names' : 'Hide Employee Names'}
          </button>

          <button className="tb-invoice-btn-secondary" onClick={toggleLayout}>
            {isListLayout ? 'Group by Activity' : 'Show as List'}
          </button>

          <button className="tb-invoice-btn-secondary" onClick={convertToPurchaseOrder} disabled={convertingPO}>
            {convertingPO ? 'Converting...' : 'Convert to Purchase Order'}
          </button>

          {isSynced && (
            <a
              className="if-btn-success"
              href={getQuickBooksInvoiceUrl(qb.invoiceId, qb.environment)}
              target="_blank"
              rel="noopener noreferrer"
            >
              View in QuickBooks
            </a>
          )}

          <button className="if-btn-qb" onClick={createQuickBooksInvoice} disabled={syncingQB}>
            {syncingQB ? 'Syncing...' : 'Sync QuickBooks Invoice'}
          </button>
        </div>
      </div>

      {showPaymentChoice && (
        <div
          className="rd-overlay payment-popup-overlay"
          onClick={() => setShowPaymentChoice(false)}
        >
          <div className="rd-choice-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <button className="rd-choice-close" onClick={() => setShowPaymentChoice(false)}>
              <X size={18} />
            </button>

            <h3>Select Payment Method</h3>

            <div className="rd-choice-buttons">
              <button
                className="rd-choice-btn rd-stripe"
                onClick={() => {
                  setShowPaymentChoice(false);
                  setShowStripe(true);
                }}
              >
                <CreditCard size={18} /> Pay with Stripe
              </button>

              <button
                className="rd-choice-btn rd-manual"
                onClick={() => {
                  setShowPaymentChoice(false);
                  setShowManual(true);
                }}
              >
                <Banknote size={18} /> Record Manual Payment
              </button>

              <button
                className="rd-choice-btn rd-credit"
                onClick={() => {
                  setShowPaymentChoice(false);
                  setShowAddFunds(true);
                }}
              >
                <Receipt size={18} /> Apply Funds
              </button>
            </div>
          </div>
        </div>
      )}

      {showStripe && (
        <StripePaymentModal
          amount={remainingBalance}
          description={`Time Billing Invoice - ${projectId}`}
          projectId={projectId}
          targetType="timebilling"
          targetId={tbInvoiceId}
          userId={userId}
          onSuccess={loadInvoice}
          onClose={() => setShowStripe(false)}
        />
      )}

      {showManual && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setShowManual(false)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Record Manual Payment</h3>

            <label>Amount</label>
            <input
              type="number"
              step="0.01"
              value={manualForm.amount}
              onChange={(e) =>
                setManualForm({ ...manualForm, amount: sanitizeCurrencyInput(e.target.value) })
              }
            />

            <label>Method</label>
            <select
              value={manualForm.method}
              onChange={(e) => setManualForm({ ...manualForm, method: e.target.value })}
            >
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="zelle">Zelle</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="other">Other</option>
            </select>

            <label>Reference</label>
            <input
              type="text"
              value={manualForm.reference}
              onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })}
            />

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setShowManual(false)}>
                Cancel
              </button>
              <button className="rd-save" onClick={applyManualPayment}>
                Save Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddFunds && (
        <AddFundsModal
          projectId={projectId}
          targetId={tbInvoiceId}
          targetType="timebilling"
          targetRemaining={remainingBalance}
          onApplied={loadInvoice}
          onClose={() => setShowAddFunds(false)}
        />
      )}

      {editPayment && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setEditPayment(null)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Payment</h3>

            <label>Amount</label>
            <input type="number" step="0.01" value={editForm.amount}
              onChange={(e) => setEditForm({ ...editForm, amount: sanitizeCurrencyInput(e.target.value) })} />

            <label>Method</label>
            <select value={editForm.method} onChange={(e) => setEditForm({ ...editForm, method: e.target.value })}>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="zelle">Zelle</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="credit_card">Credit Card</option>
              <option value="ach">ACH</option>
              <option value="wire_transfer">Wire Transfer</option>
              <option value="venmo">Venmo</option>
              <option value="other">Other</option>
            </select>

            <label>Payment Date</label>
            <input type="date" value={editForm.paymentDate}
              onChange={(e) => setEditForm({ ...editForm, paymentDate: e.target.value })} />

            <label>Reference</label>
            <input type="text" value={editForm.reference}
              onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} />

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setEditPayment(null)}>Cancel</button>
              <button className="rd-save" onClick={saveEditPayment}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirmDeletePayment}
        title="Delete Payment"
        message={`Delete this ${confirmDeletePayment?.method || 'manual'} payment of ${formatCurrency(confirmDeletePayment?.amount)}? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          const id = confirmDeletePayment.paymentId;
          setConfirmDeletePayment(null);
          deletePaymentEntry(id);
        }}
        onCancel={() => setConfirmDeletePayment(null)}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        destructive={confirmModal.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {sendEmailModal.open && (
        <SendEmailModal
          type="invoice"
          defaultEmail={sendEmailModal.email}
          clientName={invoice.billToName || ''}
          docNumber={invoice.invoiceNumber || ''}
          docUrl={sendEmailModal.publicUrl}
          companyName={invoice.company?.name || ''}
          projectId={projectId}
          documentId={tbInvoiceId}
          feeEndpoint={`${apiUrl}/api/timebilling-invoices/${userId}/${tbInvoiceId}/processing-fee`}
          onClose={() => setSendEmailModal({ open: false, email: '', publicUrl: '' })}
          onSent={() => setSendEmailModal({ open: false, email: '', publicUrl: '' })}
        />
      )}

      {editingItem && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setEditingItem(null)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Entry</h3>

            <label>Description</label>
            <input
              type="text"
              value={editItemForm.description}
              onChange={(e) => setEditItemForm({ ...editItemForm, description: e.target.value })}
            />

            <label>Hours</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={editItemForm.hours}
              onChange={(e) => setEditItemForm({ ...editItemForm, hours: e.target.value })}
            />

            <label>Rate</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={editItemForm.rate}
              onChange={(e) => setEditItemForm({ ...editItemForm, rate: e.target.value })}
            />

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setEditingItem(null)}>Cancel</button>
              <button className="rd-save" onClick={saveEditItem}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {detailsForm && (
        <div className="rd-overlay payment-popup-overlay" onClick={() => setDetailsForm(null)}>
          <div className="rd-modal payment-popup-panel" onClick={(e) => e.stopPropagation()}>
            <h3>Invoice Details</h3>

            <label>Invoice Date</label>
            <input
              type="date"
              value={detailsForm.invoiceDate}
              onChange={(e) => setDetailsForm({ ...detailsForm, invoiceDate: e.target.value })}
            />

            <label>Due Date</label>
            <input
              type="date"
              min={detailsForm.invoiceDate || undefined}
              value={detailsForm.dueDate}
              onChange={(e) => setDetailsForm({ ...detailsForm, dueDate: e.target.value })}
            />

            <label>Contingency</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                placeholder="Label"
                value={detailsForm.contingencyLabel}
                disabled={hasPayments}
                onChange={(e) => setDetailsForm({ ...detailsForm, contingencyLabel: e.target.value })}
              />
              <input
                type="number"
                step="0.001"
                min="0"
                max="100"
                placeholder="%"
                style={{ width: 90, flexShrink: 0 }}
                value={detailsForm.contingencyPercent}
                disabled={hasPayments}
                onChange={(e) => setDetailsForm({ ...detailsForm, contingencyPercent: e.target.value })}
              />
            </div>

            <label>Discount</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                style={{ width: 90, flexShrink: 0 }}
                value={detailsForm.discountType}
                disabled={hasPayments}
                onChange={(e) => setDetailsForm({ ...detailsForm, discountType: e.target.value })}
              >
                <option value="amount">$</option>
                <option value="percent">%</option>
              </select>
              <input
                type="number"
                step="0.01"
                min="0"
                value={detailsForm.discountValue}
                disabled={hasPayments}
                onChange={(e) => setDetailsForm({ ...detailsForm, discountValue: e.target.value })}
              />
            </div>

            <label>Deposit Requested</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                style={{ width: 90, flexShrink: 0 }}
                value={detailsForm.depositType}
                onChange={(e) => setDetailsForm({ ...detailsForm, depositType: e.target.value })}
              >
                <option value="percent">%</option>
                <option value="amount">$</option>
              </select>
              <input
                type="number"
                step="0.01"
                min="0"
                value={detailsForm.depositValue}
                onChange={(e) => setDetailsForm({ ...detailsForm, depositValue: e.target.value })}
              />
            </div>

            <p style={{ fontSize: 12, color: '#6b7280', margin: '10px 0 0' }}>
              Contingency is a percentage of the subtotal. Discount is applied before tax, and the
              deposit is calculated from the invoice total.
              {hasPayments && ' Contingency and discount are locked once payments are recorded.'}
            </p>

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setDetailsForm(null)}>Cancel</button>
              <button className="rd-save" onClick={saveDetails} disabled={savingDetails}>
                {savingDetails ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
